import { AluuCurated, Category, SmmCurated } from '@apis/index'
import Button from '@components/button'
import Fields from '@components/fields'
import Grid from '@components/grid'
import Row from '@components/row'
import Wrapper from '@components/wrapper'
import { Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Spinner } from './style'

const typeOptions = [
  { title: 'Manual', key: 'manual' },
  { title: 'AllU (Game Top-Up)', key: 'aluu' },
  { title: 'SMM', key: 'smm' },
]

const CategoryForm = () => {
  const { state } = useLocation()
  const navigate = useNavigate()
  const [initialValues, setInitialValues] = useState({
    type: 'manual',
    key: '',
    title: '',
    fieldOne: '',
    fieldTwo: '',
    image: '',
    order: '0',
  })
  const [smmCategories, setSmmCategories] = useState<
    { title: string; key: string }[]
  >([])
  const [aluuGames, setAluuGames] = useState<{ title: string; key: string }[]>(
    [],
  )
  const [loadingCatalogue, setLoadingCatalogue] = useState(false)
  const [loadingImage, setLoadingImage] = useState(false)

  useEffect(() => {
    if (state) {
      setInitialValues({
        type: state.type || 'manual',
        key: state.key || '',
        title: state.title || '',
        fieldOne: state.fieldOne || '',
        fieldTwo: state.fieldTwo || '',
        image: state.image || '',
        order: String(state.order ?? 0),
      })
    } else {
      setInitialValues({
        type: 'manual',
        key: '',
        title: '',
        fieldOne: '',
        fieldTwo: '',
        image: '',
        order: '0',
      })
    }
  }, [state])

  useEffect(() => {
    const fetchSmmCatalogue = async () => {
      try {
        const response = await SmmCurated.catalogue({})
        const uniqueCategories: string[] = Array.from(
          new Set<string>((response || []).map((item: any) => item.category)),
        )
        setSmmCategories(
          uniqueCategories.map((name) => ({ title: name, key: name })),
        )
      } catch (error) {
        console.error(error)
      }
    }
    const fetchAluuGames = async () => {
      try {
        const response = await AluuCurated.games({})
        setAluuGames(
          (response || []).map((game: any) => ({
            title: game.Name,
            key: game.gamecode,
          })),
        )
      } catch (error) {
        console.error(error)
      }
    }
    const fetchAll = async () => {
      setLoadingCatalogue(true)
      try {
        await Promise.all([fetchSmmCatalogue(), fetchAluuGames()])
      } finally {
        setLoadingCatalogue(false)
      }
    }
    fetchAll()
  }, [])

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const { type, key, title, fieldOne, fieldTwo, image, order } = values
      const payload: any = {
        name: title,
        type,
        order: Number(order) || 0,
        image: image.base64
          ? {
              extension: image.extension,
              base64: image.base64,
            }
          : image,
      }
      if (type === 'manual') {
        payload.infos = {
          fields: [
            { name: fieldOne, type: 'text' },
            { name: fieldTwo, type: 'text' },
          ],
        }
      } else {
        payload.key = key
      }
      if (!!state) {
        await Category.update({
          id: state?.id,
          data: JSON.stringify(payload),
          state: 'categories',
        })
        resetForm()
        navigate('/admin/store-management/category')
      } else {
        await Category.create({
          data: JSON.stringify(payload),
          state: 'categories',
        })
        resetForm()
      }
    } catch (error) {}
  }

  return (
    <Wrapper>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <Form>
            <div style={{ display: 'flex' }}>
              <Grid styleGrid="width:200%" repeat={1}>
                <Fields.DropDown
                  titleAsKey="title"
                  valueAsKey="key"
                  options={typeOptions}
                  name="type"
                  placeholder="Select Category Type"
                  label="Category Type"
                  onChange={(value) => {
                    setFieldValue('type', value)
                    setFieldValue('key', '')
                  }}
                  styleOptionsWrapper="z-index:99999999999"
                />

                {values.type === 'manual' ? (
                  <>
                    <Fields.Input
                      name="title"
                      placeholder="Enter Category Title"
                      label="Category Title"
                    />
                    <Fields.Input
                      name="fieldOne"
                      placeholder="Enter Field One Name"
                      label="Field One Name"
                    />
                    <Fields.Input
                      name="fieldTwo"
                      placeholder="Enter Field Two Name"
                      label="Field Two Name"
                    />
                  </>
                ) : (
                  <>
                    <Fields.DropDown
                      titleAsKey="title"
                      valueAsKey="key"
                      options={
                        values.type === 'smm' ? smmCategories : aluuGames
                      }
                      name="key"
                      placeholder={
                        loadingCatalogue
                          ? 'Loading...'
                          : values.type === 'smm'
                            ? 'Select SMM Category'
                            : 'Select AllU Game'
                      }
                      label={
                        values.type === 'smm' ? 'SMM Category' : 'AllU Game'
                      }
                      hasSearch
                      disabled={loadingCatalogue || loadingImage}
                      rightComponent={
                        loadingCatalogue || loadingImage ? <Spinner /> : undefined
                      }
                      styleOptionsWrapper="z-index:99999999999"
                      onChange={async (value) => {
                        setFieldValue('key', value)
                        // Prefill with the provider's default image (fetched
                        // server-side since aluu.in blocks cross-origin <img>
                        // loading); admin can still overwrite it below.
                        if (values.type === 'aluu' && value) {
                          setLoadingImage(true)
                          try {
                            const dataUri = await AluuCurated.gameImage({ id: value })
                            if (dataUri) {
                              const [info, base64] = dataUri.split(',')
                              const extension =
                                info?.match(/data:image\/(\w+);/)?.[1] || 'webp'
                              setFieldValue('image', { info, base64, extension })
                            }
                          } catch (error) {
                            console.error(error)
                          } finally {
                            setLoadingImage(false)
                          }
                        }
                      }}
                    />
                    <Fields.Input
                      name="title"
                      placeholder="Enter Custom Category Title"
                      label="Custom Category Title"
                    />
                  </>
                )}
                <Fields.Input
                  type="number"
                  name="order"
                  placeholder="Enter Display Order"
                  label="Order"
                />
              </Grid>
              <Fields.Upload name="image" variant="banner" />
            </div>
            <br />
            <Row styleRow="justify-content:flex-end">
              {!!state && (
                <Button
                  title="Cancel"
                  justify="end"
                  height={45}
                  width={100}
                  type="submit"
                  styleButton="background:red"
                  onClick={() => {
                    navigate('/admin/store-management/category')
                    setInitialValues({
                      type: 'manual',
                      key: '',
                      title: '',
                      fieldOne: '',
                      fieldTwo: '',
                      image: '',
                      order: '0',
                    })
                  }}
                />
              )}
              <Button
                title={!!state ? 'Update' : 'Submit'}
                justify="end"
                height={45}
                width={100}
                type="submit"
              />
            </Row>
          </Form>
        )}
      </Formik>
    </Wrapper>
  )
}

export default CategoryForm
